import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMenu, FiX, FiChevronDown } = FiIcons;

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Timer for delayed dropdown hide
  const [hideTimer, setHideTimer] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setServicesOpen(false);
  }, [location]);

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Portfolio', path: '/portfolio' },
    { name: 'Pricing', path: '/pricing' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Knowledge base', path: '/knowledge' },
    { name: 'Contact', path: '/contact' },
  ];

  const serviceItems = [
    { name: 'Web hosting', path: '/services', hash: 'hosting' },
    { name: 'Email hosting', path: '/services', hash: 'email' },
    { name: 'Web design', path: '/services', hash: 'design' },
    { name: 'Domain registration', path: '/services', hash: 'domains' },
  ];

  const handleServiceClick = (item, e) => {
    e.preventDefault();
    setServicesOpen(false);
    setIsOpen(false);
    console.log('Navigating to service:', item);
    // Always navigate to services page first
    navigate('/services');
    // If there's a hash, scroll to the section after a short delay
    if (item.hash) {
      setTimeout(() => {
        const element = document.getElementById(item.hash);
        console.log('Looking for element:', item.hash, 'Found:', element);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          });
        }
      }, 300); // Increased delay to ensure page is loaded
    }
  };

  const handleServicesMainClick = (e) => {
    e.preventDefault();
    setServicesOpen(false);
    setIsOpen(false);
    navigate('/services');
  };

  const handleNavClick = (path, e) => {
    e.preventDefault();
    setIsOpen(false);
    setServicesOpen(false);
    console.log('Navigating to:', path);
    navigate(path);
  };

  // Handle mouse enter and leave for services dropdown with timer
  const handleServicesMouseEnter = () => {
    // Clear any existing hide timer
    if (hideTimer) {
      clearTimeout(hideTimer);
      setHideTimer(null);
    }
    setServicesOpen(true);
  };

  const handleServicesMouseLeave = () => {
    // Set a 1-second delay before hiding the dropdown
    const timer = setTimeout(() => {
      setServicesOpen(false);
    }, 1000);
    setHideTimer(timer);
  };

  // Clear timer on component unmount
  useEffect(() => {
    return () => {
      if (hideTimer) {
        clearTimeout(hideTimer);
      }
    };
  }, [hideTimer]);

  return (
    <nav
      className={`fixed w-full z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white shadow-lg py-2'
          : 'bg-white/95 backdrop-blur-sm py-4'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-3">
            <img
              src="https://media.phst.co.za/file/ph/primehost-logo-800.png"
              alt="Primehost"
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navItems.slice(0, 2).map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`text-base font-medium transition-colors duration-200 ${
                  location.pathname === item.path
                    ? 'text-primary-500'
                    : 'text-gray-700 hover:text-primary-500'
                }`}
              >
                {item.name}
              </Link>
            ))}

            {/* Services Dropdown */}
            <div 
              className="relative"
              onMouseEnter={handleServicesMouseEnter}
              onMouseLeave={handleServicesMouseLeave}
            >
              <button
                onClick={handleServicesMainClick}
                className={`flex items-center space-x-1 text-base font-medium transition-colors duration-200 ${
                  location.pathname === '/services'
                    ? 'text-primary-500'
                    : 'text-gray-700 hover:text-primary-500'
                }`}
              >
                <span>Services</span>
                <SafeIcon
                  icon={FiChevronDown}
                  className={`w-4 h-4 transition-transform duration-200 ${
                    servicesOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {servicesOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="absolute top-full left-0 w-56 bg-white rounded-lg border border-gray-200 py-3 z-[60]"
                  style={{
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 88, 65, 0.1)',
                    marginTop: '4px'
                  }}
                  onMouseEnter={handleServicesMouseEnter}
                  onMouseLeave={handleServicesMouseLeave}
                >
                  {serviceItems.map((item) => (
                    <button
                      key={item.name}
                      onClick={(e) => handleServiceClick(item, e)}
                      className="block w-full text-left px-5 py-3 text-base text-gray-700 hover:bg-gradient-to-r hover:from-primary-50 hover:to-primary-25 hover:text-primary-600 transition-all duration-200 border-l-4 border-transparent hover:border-primary-500"
                    >
                      {item.name}
                    </button>
                  ))}
                </motion.div>
              )}
            </div>

            {navItems.slice(2).map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`text-base font-medium transition-colors duration-200 ${
                  location.pathname === item.path
                    ? 'text-primary-500'
                    : 'text-gray-700 hover:text-primary-500'
                }`}
              >
                {item.name}
              </Link>
            ))}

            <Link
              to="/portal"
              className="bg-teal-700 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-teal-800 transition-colors duration-200"
            >
              Client portal
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-700 hover:text-primary-500 transition-colors duration-200"
            >
              <SafeIcon icon={isOpen ? FiX : FiMenu} className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden mt-4 pb-4 border-t border-gray-100"
          >
            <div className="flex flex-col space-y-3 pt-4">
              {navItems.map((item) => (
                <button
                  key={item.name}
                  onClick={(e) => handleNavClick(item.path, e)}
                  className={`text-base font-medium transition-colors duration-200 text-left ${
                    location.pathname === item.path
                      ? 'text-primary-500'
                      : 'text-gray-700 hover:text-primary-500'
                  }`}
                >
                  {item.name}
                </button>
              ))}

              <div className="border-t border-gray-100 pt-3">
                <button
                  onClick={(e) => handleNavClick('/services', e)}
                  className={`text-base font-medium transition-colors duration-200 text-left mb-2 ${
                    location.pathname === '/services'
                      ? 'text-primary-500'
                      : 'text-gray-700 hover:text-primary-500'
                  }`}
                >
                  Services
                </button>
                {serviceItems.map((item) => (
                  <button
                    key={item.name}
                    onClick={(e) => handleServiceClick(item, e)}
                    className="block w-full text-left pl-4 py-1 text-sm text-gray-700 hover:text-primary-500 transition-colors duration-200"
                  >
                    {item.name}
                  </button>
                ))}
              </div>

              <button
                onClick={(e) => handleNavClick('/portal', e)}
                className="bg-teal-700 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-teal-800 transition-colors duration-200 text-center"
              >
                Client portal
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;